/**
 * @typedef {object} AuthService
 * @property {?string} accessTokenType
 * @property {?string} accessToken
 * @property {?Date} expires
 * @property {object} claims
 * @property login
 * @property logout
 */
/**
 * Authentication service.
 * @param {*} $rootScope
 * @param {*} $http
 * @param {*} $timeout
 * @param {DataService} dataService
 * @returns {AuthService}
 */

var initAuth = {};

function AuthService($rootScope, $http, $timeout, dataService) {
    var service = {
        accessTokenType: initAuth.accessTokenType,
        accessToken: initAuth.accessToken,
        expires: initAuth.expires ? new Date(initAuth.expires) : null,
        claims: initAuth.claims,

        /**
         * Site/user login.
         * @param {string} username
         * @param {string} password
         * @return {*}
         */
        login: function(username, password) {
            return dataService.authenticate(username, password)
                .then(function(result) {
                    // Update service
                    service.accessTokenType = result.type;
                    service.accessToken = result.access_token;
                    service.expires = new Date(Math.floor(Date.now() + result.expires_in * 1000));
                    return dataService.validateToken(service.accessToken);
                })
                .then(function(claims) {
                    service.claims = claims;
                    // Save to local storage
                    localStorage.setItem('etest.auth', JSON.stringify({
                        accessTokenType: service.accessTokenType,
                        accessToken: service.accessToken,
                        expires: service.expires
                    }));
                    $http.defaults.headers.common.Authorization = 'Bearer ' + service.accessToken;
                    // Emit login event
                    $rootScope.$broadcast('auth.login');
                    return claims;
                })
                .catch(function(err) {
                    // Update service
                    service.accessTokenType = null;
                    service.accessToken = null;
                    service.expires = null;
                    service.claims = {};
                    // Remove from local storage
                    localStorage.removeItem('etest.auth');
                    delete $http.defaults.headers.common.Authorization;
                    // Emit logout event
                    $rootScope.$broadcast('auth.logout');
                    throw err;
                });
        },

        /**
         * Logout
         * @return {*}
         */
        logout: function() {
            var reset = function() {
                service.accessTokenType = null;
                service.accessToken = null;
                service.expires = null;
                service.claims = {};
                delete $http.defaults.headers.common.Authorization;
                localStorage.removeItem('etest.auth');
            };

            // Notify api
            return dataService.logout()
                .then(function() {
                    reset();
                    $rootScope.$broadcast('auth.logout');
                })
                .catch(function() {
                    reset();
                    $rootScope.$broadcast('auth.logout');
                });
        }
    };

    // Initialization
    (function() {
        if (service.accessToken) $http.defaults.headers.common.Authorization = 'Bearer ' + service.accessToken;
        $rootScope.$broadcast('auth.ready');
    })();

    return service;
}

/**
 * @typedef DataService
 * @type {object}
 * @property authenticate
 * @property validateToken
 * @property logout
 */
/**
 * Data service.
 * @param $http
 * @param $q
 * @returns {DataService}
 */
function DataService($http, $q) {
    var service = {
        etestApiUrl: null,
        contentApi: null,
        adminApi: null,

        /**
         * Authenticate the site/user.
         * @param {string} username - Client code or username.
         * @param {string} password
         * @return {*}
         */
        authenticate: function(username, password) {
            return $http.post(service.etestApiUrl + '/authenticate', { username: username, password: password})
                .then(function(response) { return response.data; })
        },

        /**
         * Validate the access token.
         * @param {string} accessToken
         * @return {*}
         */
        validateToken: function(accessToken) {
            return $http.get(service.etestApiUrl + '/validate-token', {
                headers: { Authorization: 'Bearer ' + accessToken }
            })
                .then(function(response) { return response.data; });
        },

        /**
         * Forgot password.
         * @param {string} username
         * @return {*}
         */
        forgotPassword: function(username) {
            return $http.post(service.etestApiUrl + '/forgot-password', {userName: username});
        },

        /**
         * Logout.
         * @param {string} accessToken
         * @return {*}
         */
        logout: function(accessToken) {
            return $http.delete(service.etestApiUrl + '/logout');
        },

        /**
         * Newsletter signup.
         * @param {string} email
         * @return {*}
         */
        newsletterSignup: function(email) {
            return $http.post(service.etestApiUrl + '/newsletter', {
                email: email
            });
        },

        /**
         * Post free test drive
         * @param {object} model
         * @return {*}
         */
        freeTestDrive: function(model) {
            return $http.post(service.etestApiUrl + '/free-test-drive', model);
        },

        /**
         * Post contact
         * @param {object} model
         * @return {*}
         */
        contact: function(model) {
            return $http.post(service.etestApiUrl + '/contact', model)
        },

        /**
         * Post register
         * @param {object} model
         * @return {*}
         */
        register: function(model) {
            return $http.post(service.etestApiUrl + '/register', model)
        },

        /**
         * Post SSA register
         * @param {object} model
         * @return {*}
         */
        ssaRegister: function(model) {
            return $http.post(service.etestApiUrl + '/ssa-register', model)
        },

        /**
         * Get certification test segment.
         * @param {string} name - The test segment name.
         * @returns {*}
         */
        getTestSegment: function (name) {
            var q = 'name=' + encodeURIComponent(name);
            return $http.get(service.etestApiUrl + '/certification/test-segment?' + q);
        },

        /**
         * Post certification.
         * @param {object} model
         * @return {*}
         */
        certification: function(model) {
            return $http.post(service.etestApiUrl + '/certification', model);
        },

        /**
         * Post certification results.
         * @param {*} model - The results model.
         * @returns {*}
         */
        postCertificationResults: function (id, model) {
            return $http.post(service.etestApiUrl + '/certifications/' + id + '/results', model);
        },

        /**
         * Get certification.
         * @param {*} email - The email address
         * @returns {*}
         */
        getCertification: function (email) {
            return $http.get(service.etestApiUrl + '/certifications?email=' + encodeURIComponent(email));
        },

        /**
         * Post certification results.
         * @param {number} id - The certification id.
         * @param {*} model - The results model.
         * @returns {*}
         */
        putCertification: function (id, model) {
            return $http.put(service.etestApiUrl + '/certifications/' + id, model);
        },

        /**
         * Validate email address.
         * @param {string} email
         * @return {boolean}
         */
        validEmail: function(email) {
            var re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
            return re.test(email);
        },

        /**
         * Validate phone.
         * @param {string} number
         * @return {boolean}
         */
        validPhone: function(number) {
            return true;
            /*var re = /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/im;
            return re.test(number);*/
        },

        /**
         * Validate number.
         * @param {string} number
         * @return {boolean}
         */
        validNumber: function(number) {
            var re = /^\d+$/;
            return re.test(number);
        },
        /**
         * Validate url.
         * @param {string} url
         * @return {boolean}
         */
        validURL: function(url) {
            var re = /(http|https):\/\/(\w+:{0,1}\w*@)?(\S+)(:[0-9]+)?(\/|\/([\w#!:.?+=&%@!\-\/]))?/;
            return re.test(url);
        },

        states: null,
        getStates: function() {
            return $q(function(resolve, reject) {
                if (service.states) return resolve(factory.states);
                $http.get(service.etestApiUrl + '/admin/states')
                    .then(function(result) {
                        service.states = result.data;
                        resolve(service.states)
                    }).catch(reject);
            });
        },
        findState: function(id) {
            if (!service.states) return null;
            for (var i=0; i<service.states.length; i++) {
                if (service.states[i].id === id) return service.states[i];
            }
            return null;
        },
        countries: null,
        getCountries: function() {
            return $q(function(resolve, reject) {
                if (service.countries) return resolve(factory.countries);
                $http.get(service.etestApiUrl + '/admin/countries')
                    .then(function(result) {
                        service.countries = result.data;
                        resolve(service.countries)
                    }).catch(reject);
            });
        },
        findCountry: function(id) {
            if (!service.countries) return null;
            for (var i=0; i<service.countries.length; i++) {
                if (service.countries[i].id === id) return service.countries[i];
            }
            return null;
        }
    };

    // Initialize app
    (function() {
        var integrationsText = $('script#integrations').text();
        if (!integrationsText) return;
        var integrations = JSON.parse(integrationsText);
        if (integrations.contentKey && integrations.contentUrl)
            service.contentApi = new GhostContentAPI({ url: integrations.contentUrl, key: integrations.contentKey, version: 'v3' });
        service.etestApiUrl = integrations.etestApiUrl;
    })();

    return service;
}

/**
 * Logout controller.
 * @param $rootScope
 * @param authService
 * @constructor
 */
function LogoutController($rootScope, authService) {
    authService.logout().then(function() {
        if (window.location.search) {
            var query = {};
            _.each(window.location.search.substring(1).split('&'), function(nv) {
                var parts = nv.split('=');
                query[parts[0]] = decodeURIComponent(parts[1]);
            });
            if (query['returnTo']) return window.location.href = query['returnTo'];
        }
        window.location.href = '/';
    });
}

// Define the app module
angular.module('app', [])
    .factory('dataService', ['$http', '$q', DataService])
    .factory('authService', ['$rootScope', '$http', '$timeout', 'dataService', AuthService])
    .component('logout', {
        controller: ['$rootScope', 'authService', LogoutController],
        template: '<div></div>'
    })
    .config(['$httpProvider', function($httpProvider) {
        //initialize get if not there
        if (!$httpProvider.defaults.headers.get) {
            $httpProvider.defaults.headers.get = {};
        }
        //disable IE ajax request caching
        $httpProvider.defaults.headers.get['If-Modified-Since'] = 'Mon, 26 Jul 1997 05:00:00 GMT';
        // extra
        $httpProvider.defaults.headers.get['Cache-Control'] = 'no-cache';
        $httpProvider.defaults.headers.get['Pragma'] = 'no-cache';
    }]);

angular.element(function () {
    initAuth = JSON.parse(localStorage.getItem('etest.auth'));
    if (!initAuth) {
        initAuth = {};
        return angular.bootstrap(document, ['app']);
    }

    var integrationsText = $('script#integrations').text();
    if (!integrationsText) {
        initAuth = {};
        return angular.bootstrap(document, ['app']);
    }
    var integrations = JSON.parse(integrationsText);

    $.get({
        url: integrations.etestApiUrl + '/validate-token',
        headers: {Authorization: 'Bearer ' + initAuth.accessToken}
    })
        .done(function(claims) {
            initAuth.claims = claims;
        })
        .fail(function() {
            initAuth = {};
            localStorage.removeItem('etest.auth');
        })
        .always(function() { angular.bootstrap(document, ['app']); });
});
